import { ModelOrganizations } from '../models/organizations'
import { OrganizationDataObject } from './OrganizationDataObject'

/**
 * 組織データの管理と操作を提供するマネージャークラス
 *
 * 主な機能：
 * 1. 組織データの一括読み込みと管理
 * 2. 組織の検索と取得
 * 3. 階層構造の操作
 * 4. メモリ管理（循環参照の解除、キャッシュのクリア）
 *
 * ### 使用例 (front $core.$organizationsManager の場合)
 *
 * ```typescript
 * // 組織の取得と操作
 * const org = $core.$organizationsManager.getById('xxxx-xxxx-xxxx-xxxx') // UUID
 * const ancestors = org.getAncestorsN(2)
 * const descendants = org.getDescendantsN(1)
 *
 * // 組織の検索
 * const deptOrgs = $core.$organizationsManager.findOrganizations(org => org.category === '部')
 * ```
 */
export class OrganizationsManager {
  private organizations = new Map<string, OrganizationDataObject>()
  /**
   * 初期化処理 await 用 Promise
   */
  public initPromise: Promise<void> = null

  /**
   * 組織データのクリーンアップ処理
   * - 循環参照の解除
   * - キャッシュのクリア
   * - メモリの解放
   */
  private disposeOrganizations(): void {
    if (this.organizations.size === 0) return

    // 1. 全てのオブジェクトの参照解除
    Array.from(this.organizations.values()).forEach((org) => {
      org.manager = null // 循環参照の解除
      org.hierarchyCache = null // キャッシュの解除
    })

    // 2. Mapのクリア
    this.organizations.clear()
  }

  /**
   * 全組織データの読み込み
   * - 既存データのクリーンアップ
   * - 新規データの読み込み
   */
  async loadOrganizations(orgs: ModelOrganizations[]): Promise<void> {
    // 既存のデータを破棄
    this.disposeOrganizations()

    // 新しいデータを設定
    orgs.forEach((org) => {
      this.organizations.set(org.id, new OrganizationDataObject(org, this))
    })
  }

  /**
   * クラスのクリーンアップ処理
   */
  dispose(): void {
    this.disposeOrganizations()
  }

  /**
   * IDによる組織の取得
   */
  getById(id: string): OrganizationDataObject {
    const org = this.organizations.get(id)
    if (!org) throw new Error(`Organization not found: ${id}`)
    return org
  }

  /**
   * 上位組織の取得
   */
  getAncestors(orgId: string): OrganizationDataObject[] {
    const ancestors: OrganizationDataObject[] = []
    let current = this.getById(orgId)

    while (current.parent) {
      const parent = this.organizations.get(current.parent)
      if (!parent) break
      ancestors.unshift(parent)
      current = parent
    }

    return ancestors
  }

  /**
   * 下位組織の取得
   */
  getDescendants(orgId: string): OrganizationDataObject[] {
    const descendants: OrganizationDataObject[] = []
    const stack = [this.getById(orgId)]

    while (stack.length > 0) {
      const current = stack.pop()!
      const children = this.getDirectChildren(current.id)
      descendants.push(...children)
      stack.push(...children)
    }

    return descendants
  }

  /**
   * 直接の子組織を取得
   */
  getDirectChildren(orgId: string): OrganizationDataObject[] {
    return Array.from(this.organizations.values()).filter((org) => org.parent === orgId)
  }

  /**
   * 全組織の取得
   */
  getAllOrganizations(): OrganizationDataObject[] {
    return Array.from(this.organizations.values())
  }

  /**
   * 組織の検索
   */
  findOrganizations(
    predicate: (org: OrganizationDataObject) => boolean,
  ): OrganizationDataObject[] {
    return Array.from(this.organizations.values()).filter(predicate)
  }

  /**
   * 特定のカテゴリの組織を検索
   */
  findByCategory(category: string): OrganizationDataObject[] {
    return this.findOrganizations((org) => org.category === category)
  }

  /**
   * 特定のレベルの組織を全て取得
   */
  findByLevel(level: number): OrganizationDataObject[] {
    return this.findOrganizations((org) => org.getHierarchyCache().self.level === level)
  }

  /**
   * コードによる組織の検索
   */
  findByCode(code: string): OrganizationDataObject | undefined {
    return Array.from(this.organizations.values()).find((org) => org.code === code)
  }

  /**
   * 名前による組織の検索（部分一致）
   */
  findByName(name: string): OrganizationDataObject[] {
    return this.findOrganizations((org) => org.name.includes(name))
  }

  /**
   * パスによる組織の検索
   */
  findByPath(path: string): OrganizationDataObject | undefined {
    return Array.from(this.organizations.values()).find(
      (org) => org.getHierarchyCache().self.path === path,
    )
  }

  /**
   * ルート組織の取得
   */
  getRootOrganizations(): OrganizationDataObject[] {
    return this.findOrganizations((org) => !org.parent)
  }

  /**
   * 末端組織の取得
   */
  getLeafOrganizations(): OrganizationDataObject[] {
    return this.findOrganizations((org) => org.getHierarchyCache().self.isLeaf)
  }
}
